/**
 * PaginationHandler
 * For client-side manual pagination (with react table)
 */

import dayjs from "dayjs";
import _ from "lodash";

export class PaginationHandler {
    #originalRows;
    #statuslessRows;
    #queryStatus;
    #queryStatusField;
    #queryString;
    #queryStringField;
    #queryDate;
    #queryDateField;
    #queryArrayField;
    #queryArrayAttr;
    #queryArrayValue;
    #queryCategory;
    #queryCategoryField;
    rows;
    pageIndex = 0;
    pageSize = 10;

    constructor(rows, pageIndex, pageSize) {
        this.#originalRows = rows;
        if (rows) this.rows = rows;
        if (pageIndex) this.pageIndex = pageIndex;
        if (pageSize) this.pageSize = pageSize;
    }

    // getters
    getRows() {
        const startIndex = this.pageIndex * this.pageSize;
        const currPageRows = this.rows.slice(
            startIndex,
            startIndex + this.pageSize
        );
        return currPageRows;
    }

    getTotalPages() {
        return Math.ceil(this.rows.length / this.pageSize);
    }

    getTotalRows() {
        return this.rows.length;
    }

    // edge case for status filtering
    getStatuslessRows() {
        return this.#statuslessRows;
    }

    // setters
    setRows(rows) {
        this.#originalRows = rows;
        this.#statuslessRows = rows;
        this.rows = rows;
        return this;
    }

    setPageIndex(pageIndex) {
        this.pageIndex = pageIndex;
        return this;
    }

    setPageSize(pageSize) {
        this.pageSize = pageSize;
        return this;
    }

    setQueryString(queryStringField, queryString) {
        this.#queryStringField = queryStringField;
        this.#queryString = queryString;
        return this;
    }

    setQueryDate(queryDateField, queryDate) {
        this.#queryDateField = queryDateField;
        this.#queryDate = queryDate;
        return this;
    }

    setQueryStatus(queryStatusField, queryStatus) {
        this.#queryStatusField = queryStatusField;
        this.#queryStatus = queryStatus;
        return this;
    }

    setQueryCategory(queryCategoryField, queryCategory) {
        this.#queryCategoryField = queryCategoryField;
        this.#queryCategory = queryCategory;
        return this;
    }

    setQueryArray(queryArrayField, queryArrayAttr, queryArrayValue) {
        this.#queryArrayField = queryArrayField;
        this.#queryArrayAttr = queryArrayAttr;
        this.#queryArrayValue = queryArrayValue;
        return this;
    }

    // resetters
    resetRows() {
        this.rows = this.#originalRows;
        this.#statuslessRows = this.#originalRows;
        return this;
    }

    resetFilters() {
        this.#queryString = "";
        this.#queryDate = null;
        this.#queryStatus = null;
        this.#queryArrayValue = "";
        this.#queryCategory = null;
        return this;
    }

    // filters
    #filterRowsByStatus(field, queryStatus) {
        this.#queryStatusField = field;
        this.#queryStatus = queryStatus;
        const filteredRows = this.rows.filter(
            (row) => _.get(row, field) === queryStatus
        );
        this.rows = filteredRows;
    }

    #filterRowsByCategory(field, queryCategory) {
        this.#queryCategoryField = field;
        this.#queryCategory = queryCategory;
        const filteredRows = this.rows.filter(
            (row) => _.get(row, field) === queryCategory
        );
        this.rows = filteredRows;
    }

    #filterRowsByKeyword(field, queryString) {
        if (!Array.isArray(field)) field = [field];
        this.#queryStringField = field;
        this.#queryString = queryString;
        const filteredRows = this.rows.filter((row) =>
            // wrap in try catch in case of bad data
            field.some((f) => {
                try {
                    return _.get(row, f).toLowerCase().includes(queryString.toLowerCase());
                } catch (error) {
                    return false;
                }
            })
        );
        const filteredStatuslessRows = this.#statuslessRows.filter((row) =>
            // wrap in try catch in case of bad data
            field.some((f) => {
                try {
                    return _.get(row, f).toLowerCase().includes(queryString.toLowerCase());
                } catch (error) {
                    return false;
                }
            })
        );
        this.rows = filteredRows;
        this.#statuslessRows = filteredStatuslessRows;
    }

    #filterRowsByYearAndMonth(field, queryDate) {
        this.#queryDateField = field;
        this.#queryDate = queryDate;
        const year = dayjs(queryDate).year();
        const month = dayjs(queryDate).month();
        const filteredRows = this.rows.filter(
            (row) =>
                year === dayjs(_.get(row, field)).year() &&
                month === dayjs(_.get(row, field)).month()
        );
        const filteredStatuslessRows = this.rows.filter(
            (row) =>
                year === dayjs(_.get(row, field)).year() &&
                month === dayjs(_.get(row, field)).month()
        );
        this.rows = filteredRows;
        this.#statuslessRows = filteredStatuslessRows;
    }

    #filterRowsByArray(field, queryArrayAttr, queryArrayValue) {
        this.#queryArrayField = field;
        this.#queryArrayAttr = queryArrayAttr;
        this.#queryArrayValue = queryArrayValue;
        const filteredRows = this.rows.filter(
            (row) =>
                 _.get(row, field).some(v => v[queryArrayAttr].toString() === queryArrayValue.toString())
        );
        const filteredStatuslessRows = this.#statuslessRows.filter(
            (row) =>
                _.get(row, field).some(v => v[queryArrayAttr].toString() === queryArrayValue.toString())
        );

        this.rows = filteredRows.sort((a, b) => {
            let aMapping = a.categories.find(a => (a[queryArrayAttr] === queryArrayValue));
            let bMapping = b.categories.find(b => (b[queryArrayAttr] === queryArrayValue));
            if (!aMapping || !bMapping) return 0;
            if (a.status < b.status) {
                return 1;
            }
            if (a.status > b.status) {
                return -1;
            }
            if (a.status === b.status) {
                if (aMapping.seq < bMapping.seq) {
                    return -1;
                }
                if (aMapping.seq > bMapping.seq) {
                    return 1;
                }
                return 0;
            }
            return 0;
        });
        this.#statuslessRows = filteredStatuslessRows.sort((a, b) => {

            let aMapping = a.categories.find(a => (a[queryArrayAttr] === queryArrayValue));
            let bMapping = b.categories.find(b => (b[queryArrayAttr] === queryArrayValue));
            if (!aMapping || !bMapping) return 0;
            if (a.status < b.status) {
                return 1;
            }
            if (a.status > b.status) {
                return -1;
            }
            if (a.status === b.status) {
                if (aMapping.seq < bMapping.seq) {
                    return -1;
                }
                if (aMapping.seq > bMapping.seq) {
                    return 1;
                }
                return 0;
            }
            return 0;
        });
    }

    filterRowsByAll() {
        if (this.#queryString !== undefined && this.#queryStringField)
            this.#filterRowsByKeyword(this.#queryStringField, this.#queryString);
        if (
            this.#queryStatus !== undefined &&
            this.#queryStatus !== null &&
            this.#queryStatusField
        )
            this.#filterRowsByStatus(this.#queryStatusField, this.#queryStatus);
        if (this.#queryCategory !== undefined && this.#queryCategoryField)
            this.#filterRowsByCategory(this.#queryCategoryField, this.#queryCategory);
        // edge case when query status === null (i.e. all)
        else if (this.#queryStatus === null && this.#queryStatusField)
            this.rows = this.#statuslessRows;
        if (this.#queryDate !== undefined && this.#queryDateField)
            this.#filterRowsByYearAndMonth(this.#queryDateField, this.#queryDate);
        if (this.#queryArrayValue !== undefined && this.#queryArrayField && this.#queryArrayAttr)
            this.#filterRowsByArray(this.#queryArrayField, this.#queryArrayAttr , this.#queryArrayValue);
        return this;
    }
}